<template>
  <modal
    ref="modal"
    :show="showModifierModal"
    :width="700"
    full-height
    effect="fade"
    @closeOnEscape="closeModal"
  >
    <div
      slot="modal-header"
      class="modal-header"
    >
      <button
        type="button"
        class="close"
        @click="closeModal()"
      >
        <span>&times;</span>
      </button>
      <h4 class="modal-title center">
        Select Modifiers
      </h4>
    </div>
    <div
      slot="modal-body"
      class="modal-body"
    >
      <div
        v-show="errorMessage"
        ref="errorMessage"
        class="alert alert-danger"
      >
        <button
          class="close"
          @click="clearError()"
        />
        <span>{{ errorMessage }}</span>
      </div>
      <div
        v-if="loadingModifiers"
        class="height-100 display-flex align-items-center"
      >
        <loading-screen
          :show="loadingModifiers"
          :color="'#2C3E50'"
          :display="'inline'"
        />
      </div>
      <template v-else-if="storeModifiers.length">
        <div
          v-if="loadingSelectedModifierTiers"
          class="height-100 display-flex align-items-center"
        >
          <loading-screen
            :show="loadingSelectedModifierTiers"
            :color="'#2C3E50'"
            :display="'inline'"
          />
        </div>
        <div
          v-else-if="filtered.length"
          class="table-scrollable"
        >
          <table class="table">
            <thead>
              <tr>
                <th>
                  <div class="md-checkbox has-success">
                    <input
                      id="select_all"
                      v-model="allSelected"
                      type="checkbox"
                      class="md-check"
                    >
                    <label for="select_all">
                      <span class="inc" />
                      <span class="check" />
                      <span class="box" />
                    </label>
                  </div>
                </th>
                <th> Image </th>
                <th> Name </th>
                <th> Description </th>
                <th> Max </th>
                <th> Min </th>
                <th> Status </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="modifier in filtered"
                :key="modifier.id"
              >
                <td>
                  <div class="md-checkbox has-success">
                    <input
                      :id="'modifier_checkbox_' + modifier.id"
                      v-model="modifier.selected"
                      type="checkbox"
                      class="md-check"
                    >
                    <label :for="'modifier_checkbox_' + modifier.id">
                      <span class="inc" />
                      <span class="check" />
                      <span class="box" />
                    </label>
                  </div>
                </td>
                <td>
                  <img
                    :src="modifier.image_url"
                    width="30"
                    height="30"
                  >
                </td>
                <td> {{ modifier.name }} </td>
                <td> {{ modifier.desc }} </td>
                <td> {{ modifier.max }} </td>
                <td> {{ modifier.min }} </td>
                <td>
                  <span v-if="modifier.status === 1">
                    Available
                  </span>
                  <span v-if="modifier.status === 0">
                    Sold Out
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <no-results
          v-else
          :show="true"
          :custom="true"
          :text="'There are no modifiers that match the selected criteria'"
        />
      </template>
      <no-results
        v-else
        :type="'modifiers'"
      />
    </div>
    <div
      slot="modal-footer"
      class="modal-footer"
    >
      <div class="display-flex flex-direction-column align-items-flex-end">
        <div class="width-100 display-flex flex-wrap-wrap align-items-flex-start flex-wrap-wrap mb-1em">
          <el-input
            v-model="searchTerm"
            size="small"
            clearable
            placeholder="Search"
            class="flex-basis-050 max-width-200px"
          />
          <el-select
            v-model="selectedModifierTierIds"
            multiple
            clearable
            collapse-tags
            placeholder="Tier"
            size="small"
            class="flex-basis-025 ml-1em"
          >
            <el-option
              v-for="tier in modifierTiers"
              :key="tier.id"
              :label="tier.name"
              :value="tier.id"
            />
          </el-select>
          <div class="flex-basis-025 display-flex flex-direction-column align-items-flex-start ml-1em">
            <el-checkbox
              v-model="showSelectedOnly"
              class=""
            >
              Selected only
            </el-checkbox>
            <p
              class="my-0"
            >
              {{ selectedCount }} selected
            </p>
          </div>
        </div>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="!storeModifiers.length || applying"
          @click="applyModifiersToItem()"
        >
          Save
          <i
            v-show="applying"
            class="fa fa-spinner fa-pulse fa-fw"
          />
        </button>
      </div>
    </div>
  </modal>
</template>

<script>
import Modal from '../../../modules/Modal'
import ModifiersFunctions from '../../../../controllers/Modifiers'
import ajaxErrorHandler from '@/controllers/ErrorController'
import ModifierTiersFunctions from '@/controllers/ModifierTiers'
import LoadingScreen from '@/components/modules/LoadingScreen'
import NoResults from '@/components/modules/NoResults'

export default {
  components: {
    LoadingScreen,
    Modal,
    NoResults
  },
  props: {
    previouslySelected: {
      type: Array,
      default: () => []
    },
    selectedItemId: {
      type: Number,
      default: 0
    }
  },
  data () {
    return {
      showModifierModal: false,
      applying: false,
      errorMessage: '',
      storeModifiers: [],
      modifierTiers: [],
      selectedModifierTierIds: [],
      searchTerm: '',
      showSelectedOnly: false,
      loadingModifiers: false
    }
  },
  computed: {
    selectedCount () {
      return this.storeModifiers.filter(modifier => modifier.selected).length
    },
    allSelected: {
      get () {
        return this.filtered.length && !this.filtered.some(item => !item.selected)
      },
      set (value) {
        this.filtered.forEach(item => {
          item.selected = value
        })
      }
    },
    loadingSelectedModifierTiers () {
      return this.selectedModifierTiers.some(tier => tier.loadingModifiers)
    },
    selectedModifierTiers () {
      return this.modifierTiers.filter(tier => this.selectedModifierTierIds.includes(tier.id))
    },
    filtered () {
      let all = this.storeModifiers
      if (this.showSelectedOnly) {
        all = all.filter(modifier => modifier.selected)
      }
      if (this.selectedModifierTiers.length) {
        all = all.filter(modifier => {
          return this.selectedModifierTiers.some(tier => tier.modifiers.includes(modifier.id))
        })
      }
      if (this.searchTerm.length) {
        all = all.filter(modifier => {
          return `${modifier.name} ${modifier.desc}`.toLowerCase().includes(this.searchTerm.toLowerCase())
        })
      }
      return all
    }
  },
  created () {
    this.getStoreModifiers()
    this.getModifierTiers()
  },
  mounted () {
    this.showModifierModal = true
  },
  methods: {
    /**
     * To get a list of modifier tiers.
     * @function
     * @returns {object} - A promise that will either return an error message or perform an action.
     */
    getModifierTiers () {
      this.loadingModifierTiers = true
      var _this = this
      return ModifierTiersFunctions.getModifierTiers()
        .then(response => {
          _this.modifierTiers = response.payload
            .map(tier => {
              return {
                ...tier,
                loadingModifiers: false,
                modifiers: []
              }
            })
            .sort(
              (a, b) => a.name > b.name
            )

          for (const tier of _this.modifierTiers) {
            _this.getModifierTierDetails(tier)
          }

          _this.loadingModifierTiers = false
        })
        .catch(reason => {
          _this.loadingModifierTiers = false
          ajaxErrorHandler({
            reason,
            errorText: "We couldn't fetch Modifier Tiers",
            errorName: 'errorMessage',
            vue: _this
          })
        })
    },
    /**
     * To fetch a list of modifiers for a tier
     * @function
     * @param {object} tier - The tier to get modifiers for
     * @returns {object} - A network call promise
     */
    getModifierTierDetails (tier) {
      let _this = this
      tier.loadingModifiers = true
      return ModifierTiersFunctions.getModifierTierDetails(tier)
        .then(response => {
          tier.modifiers = response.payload.map(modifier => modifier.id)
        })
        .catch(reason => {
          ajaxErrorHandler({
            reason,
            errorName: 'errorMessage',
            errorText: "We couldn't fetch modifiers for this tier",
            vue: _this
          })
        })
        .finally(() => {
          tier.loadingModifiers = false
        })
    },
    /**
     * To clear the error message.
     * @function
     * @returns {undefined}
     */
    clearError () {
      this.errorMessage = ''
    },
    /**
     * To just close the modal and emit the selected item to the parent.
     * @function
     * @returns {undefined}
     */
    closeModal () {
      this.$emit('deactivateModifierModal', this.selectedItemId)
    },
    /**
     * To get a list of modifier categories for a store.
     * @function
     * @returns {object} - A promise that will either return an error message or perform an action.
     */
    getStoreModifiers () {
      var _this = this
      _this.loadingModifiers = true
      _this.storeModifiers = []
      ModifiersFunctions.getStoreModifiers(
        _this.$root.activeLocation.id
      )
        .then(response => {
          _this.storeModifiers = response.payload.map(modifier => {
            return {
              ...modifier,
              selected: _this.previouslySelected.includes(modifier.id)
            }
          })
        })
        .catch(reason => {
          ajaxErrorHandler({
            reason,
            errorText: 'We could not fetch modifiers',
            errorName: 'errorMessage',
            vue: _this,
            containerRef: 'modal'
          })
        })
        .finally(() => {
          _this.loadingModifiers = false
        })
    },
    /**
     * To apply some of the existing modifiers to an item.
     * @function
     * @returns {object} - A promise that will either return an error message or perform an action.
     */
    applyModifiersToItem () {
      this.applying = true
      var _this = this
      var modifiersToBeApplied = []
      for (var k = 0; k < _this.storeModifiers.length; k++) {
        if (_this.storeModifiers[k].selected) {
          modifiersToBeApplied.push(_this.storeModifiers[k])
        }
      }
      ModifiersFunctions.applyModifiersToItem(
        _this.selectedItemId,
        modifiersToBeApplied,
        _this.$root.appId,
        _this.$root.appSecret,
        _this.$root.userToken
      )
        .then(response => {
          if (response.code === 200 && response.status === 'ok') {
            _this.showUpdateSuccess(response.payload)
            _this.closeModal()
          }
        })
        .catch(reason => {
          ajaxErrorHandler({
            reason,
            errorText: 'We could not fetch modifiers',
            errorName: 'errorMessage',
            vue: _this,
            containerRef: 'modal'
          })
        })
        .finally(() => {
          _this.applying = false
        })
    },
    /**
     * To notify user of the outcome of the call
     * @function
     * @param {object} payload - The payload object from the server response
     * @returns {undefined}
     */
    showUpdateSuccess (payload = {}) {
      let title = 'Success'
      let text = 'The Modifiers have been saved'
      let type = 'success'

      if (payload && payload.pending_approval) {
        title = 'Approval Required'
        text = 'The changes have been sent for approval'
        type = 'info'
      }

      this.$swal({
        title,
        text,
        type
      })
    },
    /**
     * To select all modifiers on an item
     * @function
     * @returns {undefined}
     */
    selectAllModifiers () {
      this.allSelected = !this.allSelected
      for (let i = 0; i < this.storeModifiers.length; i++) {
        this.storeModifiers[i].selected = this.allSelected
      }
    }
  }
}
</script>
